import { createContext, useContext, useState } from "react";
import { login as loginService, logoutService, validateSessionService } from "../../services/authService";
import {
  buscaEmulacaoAtual,
  encerraEmulacao as encerraEmulacaoService,
  iniciaEmulacao as iniciaEmulacaoService,
  EscolhaDeEmulacao,
  PERFIL_QUE_PODE_EMULAR,
} from "../../services/emulacaoService";
import {
  EmulacaoAtiva,
  UsuarioDaSessao,
  gravaEmulacao,
  gravaUsuarioDaSessao,
  leEmulacao,
  leUsuarioDaSessao,
  limpaUsuarioDaSessao,
} from "./sessaoDoUsuario";
import LoadingOverlay from "../../components/Loading";
import { getErrorMessage } from '../../locales/translationUtils';

interface AuthContextProps {
  user: any;
  loginAction: (data: User) => Promise<void>;
  validateSession: () => Promise<boolean>;
  /** Encerra a sessao no servidor e limpa o perfil guardado no navegador. */
  logoutAction: () => Promise<void>;
  isAuthenticated: boolean;
  /** Indica que o login foi feito com a senha padrão e a troca ainda é obrigatória. */
  precisaTrocarSenhaPadrao: boolean;
  /** Encerra a exigência de troca, depois que o usuário cadastra a senha dele. */
  senhaPadraoTrocada: () => void;
  /** Emulação de perfil/cliente ativa, ou null quando o usuário está no próprio perfil. */
  emulacao: EmulacaoAtiva | null;
  /** Perfil que vale para a interface: o emulado quando há emulação, senão o real. */
  perfilEfetivo: string | null;
  /** Indica que o usuário é master de verdade e portanto pode emular. */
  podeEmular: boolean;
  /** Liga a emulação de perfil e/ou cliente. */
  aplicaEmulacao: (escolha: EscolhaDeEmulacao) => Promise<void>;
  /** Desliga a emulação. */
  encerraEmulacaoAtual: () => Promise<void>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

interface User {
  username: string;
  password: string;
  id: number;
  rememberMe: boolean;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  // O perfil nasce do espelho guardado no navegador para a sessao sobreviver a um F5
  // ou a abertura direta de uma rota protegida. Sem isso o boot ve user=null, o
  // ProtectedRoute quica em /login e a tela de login sempre volta pra "/", perdendo o
  // destino. Quem sustenta a sessao de verdade e o cookie: se ele ja expirou, a
  // primeira chamada de API responde 401 e o interceptor (services/api.ts) manda pro
  // login limpando o espelho velho.
  const [user, setUser] = useState<User | UsuarioDaSessao | null>(() => leUsuarioDaSessao());
  const [precisaTrocarSenhaPadrao, setPrecisaTrocarSenhaPadrao] = useState<boolean>(false);
  // Inicia com o valor espelhado no navegador para a barra ja nascer certa num F5,
  // antes de a consulta ao servidor responder.
  const [emulacao, setEmulacao] = useState<EmulacaoAtiva | null>(leEmulacao());

  const loginAction = async (data: User) => {
    try {
      setLoading(true);
      const userData = await loginService(data);

      // O "manter conectado" ja foi resolvido no cookie pelo servidor; aqui so guardamos
      // o perfil para a interface, sempre no mesmo lugar.
      gravaUsuarioDaSessao(userData);

      // Cookie novo, emulacao zerada: o login sempre entra no proprio perfil.
      guardaEmulacao(null);

      setUser({ ...data, id: userData.id });
      setPrecisaTrocarSenhaPadrao(!!userData.senhaPadrao);
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const senhaPadraoTrocada = () => setPrecisaTrocarSenhaPadrao(false);

  // O perfil real nunca muda com a emulacao - e por ele que se decide quem pode emular
  // e para onde voltar ao desligar.
  const perfilReal = leUsuarioDaSessao()?.permission ?? null;

  /**
   * Sai do sistema.
   *
   * A limpeza local acontece mesmo se a chamada ao servidor falhar: o usuario pediu
   * para sair e a tela precisa obedecer. Mas a chamada vem primeiro, porque so ela
   * invalida o cookie - sem isso, sair no navegador deixaria a sessao viva no servidor.
   */
  const logoutAction = async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error("Nao foi possivel encerrar a sessao no servidor:", error);
    } finally {
      limpaUsuarioDaSessao();
      setUser(null);
      setEmulacao(null);
      setPrecisaTrocarSenhaPadrao(false);
    }
  };

  /** Mantem o estado e o espelho no navegador em sincronia. */
  const guardaEmulacao = (situacao: EmulacaoAtiva | null) => {
    const ativa = situacao?.emulando ? situacao : null;

    gravaEmulacao(ativa);
    setEmulacao(ativa);
  };

  const aplicaEmulacao = async (escolha: EscolhaDeEmulacao) => {
    guardaEmulacao(await iniciaEmulacaoService(escolha));
  };

  const encerraEmulacaoAtual = async () => {
    guardaEmulacao(await encerraEmulacaoService());
  };

  /**
   * Pergunta ao servidor se o cookie de autenticacao ainda vale.
   *
   * Quando nao vale, apaga o perfil guardado no navegador: ele sobrevive ao cookie e,
   * se ficar para tras, as telas continuam achando que ha alguem logado.
   */
  const validateSession = async () => {
    const userData = await validateSessionService();

    if (userData) {
      setUser(userData);
      // A emulacao vive no cookie e sobrevive ao F5: precisa ser relida junto.
      guardaEmulacao(await buscaEmulacaoAtual());
    } else {
      limpaUsuarioDaSessao();
      setUser(null);
      setEmulacao(null);
    }

    return !!userData;
  };
  

  return (
    <AuthContext.Provider value={{
      user,
      loginAction,
      validateSession,
      logoutAction,
      isAuthenticated: !!user,
      precisaTrocarSenhaPadrao,
      senhaPadraoTrocada,
      emulacao,
      perfilEfetivo: emulacao?.perfilEmulado ?? perfilReal,
      podeEmular: perfilReal === PERFIL_QUE_PODE_EMULAR,
      aplicaEmulacao,
      encerraEmulacaoAtual
    }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) { 
    throw new Error(getErrorMessage("wrongUseProviderError", 
      { 
        auth: useAuth?.name, 
        providerName: AuthProvider?.name
      }
    ));
  }
  return context;
};
